import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009';

export type NetworkQuality = 'good' | 'slow' | 'offline' | 'unknown';

export interface NetworkHealthState {
  quality: NetworkQuality;
  pingMs: number | null;
  lastCheck: number | null;
}

const PING_INTERVAL_MS = 8_000;
const SLOW_THRESHOLD_MS = 1500;
const PING_TIMEOUT_MS = 5_000;

async function measurePing(): Promise<number> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
  const start = performance.now();
  try {
    await fetch(`${API_URL}/ping`, {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-store',
    });
    return Math.round(performance.now() - start);
  } finally {
    clearTimeout(timer);
  }
}

export function useNetworkHealth(enabled = true): NetworkHealthState {
  const [state, setState] = useState<NetworkHealthState>({
    quality: 'unknown',
    pingMs: null,
    lastCheck: null,
  });
  const mountedRef = useRef(true);

  const check = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const ms = await measurePing();
      if (!mountedRef.current) return;
      setState({
        quality: ms > SLOW_THRESHOLD_MS ? 'slow' : 'good',
        pingMs: ms,
        lastCheck: Date.now(),
      });
    } catch {
      if (!mountedRef.current) return;
      setState({ quality: 'offline', pingMs: null, lastCheck: Date.now() });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return;
    check();
    const id = setInterval(check, PING_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [enabled, check]);

  return state;
}
